import {
  IOrder,
  IOrderWithItems,
  IOrderItem,
  OrdersClient,
} from "../clients/OrdersClient";
import { useStockCommitmentsStore } from "@stores/stockCommitmentsStore";
import { StockCommitmentsClient } from "@Clients/StockCommitmentsClient";
import { BackorderQueueClient } from "@Clients/BackorderQueueClient";
import { create } from "zustand";

export type OrdersStoreState = {
  orders: IOrderWithItems[];
  loading: boolean;
  error: Error | null;
  getOrdersByOrganizationId: (
    organization_id: string,
    useLoading?: boolean
  ) => Promise<void>;
  createOrder: (
    order: Omit<IOrder, "id" | "order_number" | "created_at" | "updated_at"> & {
      order_number?: string;
    }
  ) => Promise<IOrder>;
  updateOrder: (order: IOrder) => Promise<void>;
  deleteOrder: (id: string) => Promise<void>;
  getOrderById: (id: string) => Promise<IOrderWithItems | null>;
  createOrderItem: (
    orderItem: Omit<IOrderItem, "id" | "created_at" | "updated_at">
  ) => Promise<IOrderItem>;
  updateOrderItem: (
    orderItem: Omit<IOrderItem, "created_at" | "updated_at">
  ) => Promise<void>;
  deleteOrderItem: (id: string) => Promise<void>;
  updateOrderBackorderStatus: (orderId: string) => Promise<void>;
  updateOrderStatus: (
    orderId: string,
    status: "open" | "processing" | "completed" | "cancelled",
    userId: string,
    organizationId: string
  ) => Promise<void>;
  getOrdersByStatus: (
    status: "open" | "processing" | "completed" | "cancelled",
    organizationId: string
  ) => Promise<IOrder[]>;
};

export const useOrdersStore = create<OrdersStoreState>((set, get) => ({
  orders: [],
  loading: true,
  error: null,

  async getOrdersByOrganizationId(
    organization_id: string,
    useLoading = true
  ) {
    if (useLoading) {
      set({ loading: true });
    }
    try {
      // Fetch orders with customer data joined from database
      const orders = await OrdersClient.getOrdersByOrganizationId(
        organization_id,
        true // includeCustomer = true
      );
      set({ orders });
    } catch (error) {
      console.error("Error loading orders:", error);
      set({ error: error as Error });
    } finally {
      if (useLoading) {
        set({ loading: false });
      }
    }
  },

  async createOrder(
    order: Omit<IOrder, "id" | "order_number" | "created_at" | "updated_at"> & {
      order_number?: string;
    }
  ): Promise<IOrder> {
    set({ loading: true });
    try {
      // Set default backorder status
      const orderWithBackorder = {
        ...order,
        backorder_status: "none" as const,
      };

      const newOrder = await OrdersClient.createOrder(orderWithBackorder);
      set((state) => ({
        ...state,
        orders: [{ ...newOrder, order_items: [] }, ...state.orders],
      }));
      return newOrder;
    } catch (error) {
      set({ error: error as Error });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  async updateOrder(order: IOrder) {
    set({ loading: true });
    try {
      const updatedOrder = await OrdersClient.updateOrder(order);
      set((state) => ({
        ...state,
        orders: state.orders.map((o) =>
          o.id === updatedOrder.id
            ? { ...updatedOrder, order_items: o.order_items }
            : o
        ),
      }));
    } catch (error) {
      set({ error: error as Error });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  async deleteOrder(id: string) {
    set({ loading: true });
    try {
      // Release all stock commitments for this order before deleting
      const stockCommitmentsStore = useStockCommitmentsStore.getState();
      await stockCommitmentsStore.releaseAllStockCommitmentsForOrder(id);

      await OrdersClient.deleteOrder(id);
      set((state) => ({
        ...state,
        orders: state.orders.filter((o) => o.id !== id),
      }));
    } catch (error) {
      set({ error: error as Error });
    } finally {
      set({ loading: false });
    }
  },

  async getOrderById(id: string): Promise<IOrderWithItems | null> {
    try {
      // First check if we have it in the store
      const existingOrder = get().orders.find((o) => o.id === id);
      if (existingOrder) {
        return existingOrder;
      }

      // If not, fetch from API with customer data
      const order = await OrdersClient.getOrderById(id, true); // includeCustomer = true
      set((state) => ({
        ...state,
        orders: [order, ...state.orders.filter((o) => o.id !== id)],
      }));
      return order;
    } catch (error) {
      set({ error: error as Error });
      return null;
    }
  },

  async createOrderItem(
    orderItem: Omit<IOrderItem, "id" | "created_at" | "updated_at">
  ): Promise<IOrderItem> {
    try {
      // Create order item with basic information
      // Stock commitments and fulfillment queue entries are handled by the calling code
      const newOrderItem = await OrdersClient.createOrderItem({
        ...orderItem,
        backorder_quantity: 0,
        fulfillment_priority: 3, // Default priority
        quantity_fulfilled: 0,
      });

      // Update the order in the store to include the new item
      set((state) => ({
        ...state,
        orders: state.orders.map((order) =>
          order.id === orderItem.order_id
            ? {
                ...order,
                order_items: [...(order.order_items || []), newOrderItem],
              }
            : order
        ),
      }));

      // Update the overall order backorder status
      await this.updateOrderBackorderStatus(orderItem.order_id);

      return newOrderItem;
    } catch (error) {
      set({ error: error as Error });
      throw error;
    }
  },

  async updateOrderItem(
    orderItem: Omit<IOrderItem, "created_at" | "updated_at">
  ) {
    try {
      // Get the current order item to compare quantities
      const currentOrder = get().orders.find((o) => o.id === orderItem.order_id);
      const currentItem = currentOrder?.order_items?.find(
        (item) => item.id === orderItem.id
      );

      const updatedOrderItem = await OrdersClient.updateOrderItem(orderItem);

      // Handle stock commitment updates if quantity changed
      if (
        currentItem &&
        currentItem.quantity_requested !== orderItem.quantity_requested
      ) {
        const stockCommitmentsStore = useStockCommitmentsStore.getState();
        const order = get().orders.find((o) => o.id === orderItem.order_id);
        if (!order) {
          throw new Error(`Order ${orderItem.order_id} not found`);
        }

        // Get existing commitments for this order item
        const existingCommitments =
          await stockCommitmentsStore.getStockCommitmentsByOrderItemId(
            orderItem.id
          );

        // Release old commitments
        for (const commitment of existingCommitments) {
          if (commitment.status === "active") {
            await stockCommitmentsStore.releaseStockCommitment(commitment.id);
          }
        }

        const availability = await StockCommitmentsClient.checkStockAvailability(
          orderItem.product_id,
          orderItem.quantity_requested
        );
        const commitQuantity = Math.min(
          orderItem.quantity_requested,
          availability.availableQuantity
        );
        const shortfall = Math.max(
          0,
          orderItem.quantity_requested - commitQuantity
        );

        if (commitQuantity > 0) {
          await stockCommitmentsStore.commitStock(
            orderItem.order_id,
            orderItem.id,
            orderItem.product_id,
            commitQuantity,
            order.organization_id
          );
        }

        const existingBackorders =
          await BackorderQueueClient.getBackorderQueueByOrderItemId(orderItem.id);
        const backorder = existingBackorders[0];

        if (shortfall > 0) {
          if (backorder) {
            await BackorderQueueClient.updateBackorderQueue({
              id: backorder.id,
              quantity_needed: shortfall,
              status: "pending",
            });
          } else {
            await BackorderQueueClient.createBackorderQueue({
              order_id: orderItem.order_id,
              order_item_id: orderItem.id,
              product_id: orderItem.product_id,
              quantity_needed: shortfall,
              priority: 3,
              organization_id: order.organization_id,
            });
          }
        } else if (backorder) {
          await BackorderQueueClient.deleteBackorderQueue(backorder.id);
        }
      }

      // Update the order in the store
      set((state) => ({
        ...state,
        orders: state.orders.map((order) =>
          order.id === orderItem.order_id
            ? {
                ...order,
                order_items:
                  order.order_items?.map((item) =>
                    item.id === orderItem.id ? updatedOrderItem : item
                  ) || [],
              }
            : order
        ),
      }));

      // Update the overall order backorder status
      await this.updateOrderBackorderStatus(orderItem.order_id);
    } catch (error) {
      set({ error: error as Error });
    }
  },

  async deleteOrderItem(id: string) {
    try {
      // Release stock commitments for this order item before deleting
      const stockCommitmentsStore = useStockCommitmentsStore.getState();
      await stockCommitmentsStore.deleteStockCommitmentsByOrderItemId(id);

      // Delete backorder queue entries for this order item
      const { BackorderQueueClient } = await import(
        "@Clients/BackorderQueueClient"
      );
      await BackorderQueueClient.deleteBackorderQueueByOrderItemId(id);

      await OrdersClient.deleteOrderItem(id);

      // Remove the item from the store
      set((state) => ({
        ...state,
        orders: state.orders.map((order) => ({
          ...order,
          order_items:
            order.order_items?.filter((item) => item.id !== id) || [],
        })),
      }));

      // Update the overall order backorder status
      const order = get().orders.find((o) =>
        o.order_items?.some((item) => item.id === id)
      );
      if (order) {
        await this.updateOrderBackorderStatus(order.id);
      }
    } catch (error) {
      set({ error: error as Error });
    }
  },

  async updateOrderBackorderStatus(orderId: string) {
    try {
      const order = get().orders.find((o) => o.id === orderId);
      if (!order || !order.order_items) return;

      // Calculate overall backorder status
      const { calculateOrderBackorderStatus } = await import(
        "@utils/backorderUtils"
      );
      const backorderStatus = calculateOrderBackorderStatus(order.order_items);

      // Update the order with new backorder status (remove order_items before sending to API)
      const { order_items, ...orderData } = order;
      const updatedOrder = {
        ...orderData,
        backorder_status: backorderStatus,
        backorder_created_at:
          backorderStatus !== "none"
            ? new Date().toISOString()
            : order.backorder_created_at,
      };

      // Update in database (only order fields, no order_items)
      await OrdersClient.updateOrder(updatedOrder);

      // Update in store (keep order_items from original order)
      set((state) => ({
        ...state,
        orders: state.orders.map((o) =>
          o.id === orderId ? { ...updatedOrder, order_items: order_items } : o
        ),
      }));
    } catch (error) {
      console.error("Error updating order backorder status:", error);
    }
  },

  // Update order status
  updateOrderStatus: async (orderId, status, userId, organizationId) => {
    try {
      set({ loading: true, error: null });

      // Update in database
      const ordersClient = new OrdersClient();
      const updatedOrder = await ordersClient.updateOrderStatus(
        orderId,
        status,
        userId,
        organizationId
      );

      // Update in store
      set((state) => ({
        ...state,
        orders: state.orders.map((order) =>
          order.id === orderId ? { ...order, ...updatedOrder } : order
        ),
        loading: false,
      }));
    } catch (error) {
      console.error("Error updating order status:", error);
      set({ error: error as Error, loading: false });
    }
  },

  // Get orders by status
  getOrdersByStatus: async (status, organizationId) => {
    try {
      set({ loading: true, error: null });

      const ordersClient = new OrdersClient();
      const orders = await ordersClient.getOrdersByStatus(status, organizationId);

      set({ loading: false });
      return orders;
    } catch (error) {
      console.error("Error fetching orders by status:", error);
      set({ error: error as Error, loading: false });
      return [];
    }
  },
}));
